// ─── INFORMATION RATIO EDUCATION (Phase 3) ────────────────────────────────────
// Read-only educational panel explaining the Information Ratio concept applied to
// the user's current Signal Board breadth. No recommendations, no return claims —
// it describes the statistical property that edge scales with √(independent bets).

import { Component } from "react";
import { C, s } from "./core";

const YELLOW = "#FFD700";

// Canonical signal type → { display name, factor label }. Used to list signal
// types that are NOT firing but would add an independent (different-label) view.
const SIGNAL_CATALOG = {
  "vix-skew-divergence":   { name: "VIX Term-Structure Divergence", label: "VOLATILITY REGIME" },
  "yield-curve-inversion": { name: "Yield Curve Inversion", label: "MACRO RISK-OFF" },
  "monetary-regime":       { name: "Monetary Regime",       label: "MACRO RISK-OFF" },
  "fiscal-dominance":      { name: "Fiscal Dominance",      label: "MACRO RISK-OFF" },
  "squeeze":               { name: "Short Squeeze",         label: "IDIOSYNCRATIC" },
  "dark-pool-defended":    { name: "Dark Pool Defended",    label: "INSTITUTIONAL FLOW" },
  "unusual-options":       { name: "Unusual Options",       label: "INSTITUTIONAL FLOW" },
  "congress-trade":        { name: "Congressional Trade",   label: "GOVERNMENT FLOW" },
  "ais-dark":              { name: "AIS Dark Vessels",      label: "PHYSICAL WORLD" },
  "petroleum-draw":        { name: "Petroleum Draw",        label: "PHYSICAL WORLD" },
  "sentiment-extreme":     { name: "Sentiment Extreme",     label: "SENTIMENT" },
};

// This board's signal ids → canonical type (Phase 1B bridge).
const SIGNAL_ID_TO_TYPE = {
  "vix-skew-div":         "vix-skew-divergence",
  "yield-inverted":       "yield-curve-inversion",
  "yield-flat":           "yield-curve-inversion",
  "mv-gap-inflationary":  "monetary-regime",
  "fiscal-dominance":     "fiscal-dominance",
  "unusual-options-flow": "unusual-options",
  "congress-buy-cluster": "congress-trade",
  "tanker-dark-cluster":  "ais-dark",
  "petroleum-move":       "petroleum-draw",
  "news-negative":        "sentiment-extreme",
  "news-positive":        "sentiment-extreme",
};

// Fallback labels so active signals with no catalog entry still contribute a
// factor label (matches the SignalBoard breadth fallback).
const FALLBACK_LABEL = {
  "vix-fear":             "VOLATILITY REGIME",
  "vix-complacent":       "VOLATILITY REGIME",
  "pc-put-heavy":         "INSTITUTIONAL FLOW",
  "pc-call-heavy":        "INSTITUTIONAL FLOW",
  "thermal-industrial":   "PHYSICAL WORLD",
  "large-contract-award": "GOVERNMENT FLOW",
};

// Hardcoded improvement table (positions 1-6). Values as specified.
const IR_TABLE = [
  { n: 1, mult: "1.00×", imp: "baseline" },
  { n: 2, mult: "1.41×", imp: "+41%" },
  { n: 3, mult: "1.73×", imp: "+23%" },
  { n: 4, mult: "2.00×", imp: "+15%" },
  { n: 5, mult: "2.24×", imp: "+12%" },
  { n: 6, mult: "2.45×", imp: "+9%" },
];

class PanelBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {}
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function labelOf(id) {
  const t = SIGNAL_ID_TO_TYPE[id];
  if (t && SIGNAL_CATALOG[t]) return SIGNAL_CATALOG[t].label;
  return FALLBACK_LABEL[id] || null;
}

// Distinct factor labels among active signals; unlabeled signals each count once.
function deriveIndependent(activeSignals) {
  const labels = new Set();
  let orphan = 0;
  for (const sig of activeSignals) {
    const l = labelOf(sig.id);
    if (l) labels.add(l);
    else orphan += 1;
  }
  return labels.size + orphan;
}

const SECTION_LABEL = { fontSize: 9, fontWeight: 700, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 };

function IREducationInner({ activeSignals, independentPositions, portfolioExposures }) {
  void portfolioExposures; // reserved for future factor-specific commentary

  const active = Array.isArray(activeSignals) ? activeSignals : [];
  const N = active.length;
  const M =
    typeof independentPositions === "number" ? independentPositions : deriveIndependent(active);

  const sqrtM = Math.sqrt(Math.max(0, M));
  const stepImprovement = M >= 1 ? Math.round((Math.sqrt(M + 1) / Math.sqrt(M) - 1) * 100) : null;

  // Section 4 — signal types not firing that would add a different factor label.
  const activeLabels = new Set(active.map((s) => labelOf(s.id)).filter(Boolean));
  const shownLabels = new Set();
  const missing = [];
  for (const [, meta] of Object.entries(SIGNAL_CATALOG)) {
    if (missing.length >= 4) break;
    if (activeLabels.has(meta.label) || shownLabels.has(meta.label)) continue;
    shownLabels.add(meta.label);
    missing.push(meta);
  }

  return (
    <div style={s.panel}>
      <div style={s.panelB}>
        {/* Header */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, letterSpacing: "0.15em", marginBottom: 10 }}>
          UNDERSTANDING YOUR SIGNAL EDGE
        </div>

        {/* Section 1 — The Formula */}
        <div style={{ fontSize: 12, color: C.text, textAlign: "center", marginBottom: 4 }}>
          INFORMATION RATIO = Signal Skill × √(Independent Positions)
        </div>
        <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", marginBottom: 14, lineHeight: 1.5 }}>
          Your edge grows with the square root of your truly independent positions — not the raw count.
        </div>

        {/* Section 2 — Your Current Numbers */}
        <div style={{ fontSize: 10, color: C.text, lineHeight: 1.7 }}>ACTIVE SIGNALS: {N}</div>
        <div style={{ fontSize: 10, color: C.text, lineHeight: 1.7 }}>INDEPENDENT POSITIONS: {M}</div>
        <div style={{ fontSize: 10, color: C.text, lineHeight: 1.7 }}>
          YOUR IR MULTIPLIER: √{M} = {sqrtM.toFixed(2)}
        </div>
        {N > 0 && M < N && (
          <div style={{ fontSize: 10, color: C.amber, lineHeight: 1.6, marginTop: 4 }}>
            Your {N} signals represent only {M} independent view{M === 1 ? "" : "s"}. {N - M} of your
            signals share factor exposure.
          </div>
        )}
        {N > 0 && M === N && (
          <div style={{ fontSize: 10, color: C.green, lineHeight: 1.6, marginTop: 4 }}>
            All {N} of your signals are truly independent.
          </div>
        )}

        {/* Section 3 — The Improvement Table */}
        <div style={{ ...SECTION_LABEL, marginTop: 14 }}>ADDING INDEPENDENT POSITIONS</div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 4 }}>
          <thead>
            <tr>
              {["POSITIONS", "IR MULTIPLIER", "IMPROVEMENT"].map((h, i) => (
                <th
                  key={h}
                  style={{ fontSize: 9, color: C.textDim, fontWeight: 700, textAlign: i === 0 ? "left" : "right", padding: "2px 6px" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {IR_TABLE.map((row) => {
              const current = row.n === M;
              return (
                <tr key={row.n} style={current ? { background: C.amber + "22" } : undefined}>
                  <td style={{ fontSize: 10, color: current ? C.amber : C.text, padding: "2px 6px" }}>{row.n}</td>
                  <td style={{ fontSize: 10, color: current ? C.amber : C.text, textAlign: "right", padding: "2px 6px" }}>{row.mult}</td>
                  <td style={{ fontSize: 10, color: current ? C.amber : C.textDim, textAlign: "right", padding: "2px 6px" }}>{row.imp}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Section 4 — What's Missing */}
        <div style={{ ...SECTION_LABEL, marginTop: 14 }}>SIGNALS NOT CURRENTLY FIRING</div>
        {missing.length === 0 ? (
          <div style={{ fontSize: 10, color: C.textDim }}>
            Every independent factor family is already represented.
          </div>
        ) : (
          missing.map((meta) => (
            <div key={meta.name} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10, color: C.textDim }}>● {meta.name}</div>
              <div style={{ fontSize: 10, color: C.textDim, marginLeft: 12 }}>
                Would add 1 independent position{stepImprovement != null ? ` (+${stepImprovement}% IR)` : ""}
              </div>
            </div>
          ))
        )}

        {/* Section 5 — The Breadth Note */}
        <div style={{ ...SECTION_LABEL, marginTop: 14 }}>THE KEY INSIGHT</div>
        <div style={{ fontSize: 10, color: C.textDim, fontStyle: "italic", lineHeight: 1.6 }}>
          5 correlated signals = 1 independent position. The Information Ratio rewards breadth, not
          volume. A signal that confirms an existing view adds confidence but not additional edge.
        </div>

        {/* Disclaimer */}
        <div style={{ fontSize: 10, fontWeight: 700, color: YELLOW, marginTop: 14, lineHeight: 1.6 }}>
          The Information Ratio framework is educational. It describes statistical properties of signal
          independence, not guaranteed returns. Not a recommendation.
        </div>
      </div>
    </div>
  );
}

export function IREducationPanel(props) {
  return (
    <PanelBoundary>
      <IREducationInner {...props} />
    </PanelBoundary>
  );
}
